import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faProjectDiagram,
  faUser,
  faFileAlt,
  faCalendarAlt,
  faCheckCircle,
  faSave,
  faTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../../services/projectService';
import TenantSelector from '../common/TenantSelector';
import { useTheme } from '../../contexts/ThemeContext';

const ProjectForm = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    status: 'planning',
    tenant: null
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await projectService.createProject(formData);
      alert('Project created successfully!');
      navigate('/projects');
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.response?.data?.message || 'Failed to create project. Please try again.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <div>
      <h1 className={`text-3xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <FontAwesomeIcon icon={faProjectDiagram} className="mr-3 text-blue-600" />
        Create New Project
      </h1>

      <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
        {error && (
          <div className={`mb-4 px-4 py-3 rounded ${darkMode ? 'bg-red-900/30 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="name">
              <FontAwesomeIcon icon={faProjectDiagram} className="mr-2 text-blue-600" />
              Project Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
              required
              disabled={loading}
              placeholder="Enter project name"
            />
          </div>

          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="client">
              <FontAwesomeIcon icon={faUser} className="mr-2 text-green-600" />
              Client Name *
            </label>
            <input
              type="text"
              id="client"
              name="client"
              value={formData.client}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
              required
              disabled={loading}
              placeholder="Enter client name"
            />
          </div>

          <div className="mb-4">
            <label className={`block text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="description">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-purple-600" />
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
              required
              disabled={loading}
              placeholder="Enter project description"
            ></textarea>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className={`block text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="startDate">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-orange-600" />
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                disabled={loading}
              />
            </div>

            <div>
              <label className={`block text-sm font-bold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} htmlFor="status">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-indigo-600" />
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                disabled={loading}
              >
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <TenantSelector
              value={formData.tenant}
              onChange={(val) => setFormData({ ...formData, tenant: val })}
              disabled={loading}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className={`px-4 py-2 rounded-md transition-colors flex items-center ${darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center"
              disabled={loading}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="mr-2" />
              )}
              {loading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;